import {
  CreateOrderResponse,
  GetOrdersParams,
  Order
} from '@/store/modules/order/order.types';
import {
  ApiState,
  PaginatedResponse,
  PaginatorSpecs,
  SortOrder,
  SortSpecs
} from '@/store/types/general.types';
import { AxiosResponse } from 'axios';
import { Component, Vue, Watch } from 'vue-property-decorator';
import { Action } from 'vuex-class';

// @ts-ignore
@Component
export default abstract class OrdersMixin extends Vue {
  @Action('order/retryPayment')
  public retryPayment!: (orderId: number) => Promise<AxiosResponse | any>;

  public perPage = 50;
  public page = 1;
  public sortColumn = 'createdAt';
  public sortOrder: SortOrder = SortOrder.DESC;
  public searchQuery = '';

  // --- Abstract properties that the consuming component MUST implement ---
  // This creates a contract that the component must fulfill with its own getter.
  abstract get ordersData(): PaginatedResponse<Order> | undefined;
  abstract get apiStateForOrders(): ApiState;

  abstract fetchOrdersAction(options: GetOrdersParams): void;

  // These will now correctly use the 'ordersData' and 'apiStateForOrders' getters
  // that are implemented by the component.
  get isLoading(): boolean {
    if (!this.apiStateForOrders) {
      return false;
    }
    return this.apiStateForOrders.loading && this.items.length === 0;
  }

  get items(): Order[] {
    return (this.ordersData && this.ordersData.items) || [];
  }

  get totalCount(): number {
    return (this.ordersData && this.ordersData.totalItems) || 0;
  }

  // --- Shared Lifecycle Hooks and Methods (Unchanged) ---
  public mounted(): void {
    this.fetchData();
  }

  @Watch('searchQuery')
  public onSearchQueryChanged(): void {
    this.page = 1;
    this.fetchData();
  }

  public fetchData(): void {
    this.fetchOrdersAction({
      limit: this.perPage,
      page: this.page,
      searchQuery: this.searchQuery,
      sortOrder: this.sortOrder,
      sortColumn: this.sortColumn
    });
  }

  public handlePaginator(paginator: PaginatorSpecs): void {
    this.perPage = paginator.perPage;
    this.page = paginator.page;
    this.fetchData();
  }

  public handleSort(sort: SortSpecs): void {
    this.sortColumn = sort.sortColumn;
    this.sortOrder = sort.sortOrder;
    this.fetchData();
  }

  public async handleRetryPayment({ paymentId }: { paymentId: number }) {
    const response: CreateOrderResponse = await this.retryPayment(paymentId);

    if (response.paymentRequired && response.purchasePaymentUrl) {
      return (window.location.href = response.purchasePaymentUrl);
    }

    return this.errorToast();
  }

  public errorToast() {
    const responseText =
      'Unable to connect to payment gateway. Please try again later.';

    return this.$buefy.dialog.confirm({
      title: `Error`,
      message: `<p class="subtitle"> "${responseText}"<p>`,
      confirmText: 'Ok',
      type: 'is-primary'
    });
  }
}
